import { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Gift, Check, AlertCircle, Loader2, QrCode } from 'lucide-react';
import { QRCodeSVG } from 'qrcode.react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const VoucherViewPage = () => {
  const { voucherId } = useParams();
  const [voucher, setVoucher] = useState(null);
  const [status, setStatus] = useState('loading');
  const [error, setError] = useState('');

  useEffect(() => {
    fetchVoucher();
  }, [voucherId]);

  const fetchVoucher = async () => {
    try {
      setStatus('loading');
      const response = await axios.get(`${API}/vouchers/public/${voucherId}`);
      setVoucher(response.data);
      setStatus('success');
    } catch (error) {
      console.error('Error fetching voucher:', error);
      setError(error.response?.data?.detail || 'Voucher no encontrado');
      setStatus('error');
    }
  };

  if (status === 'loading') {
    return (
      <div className="min-h-screen gradient-hero flex items-center justify-center p-4">
        <motion.div 
          className="text-center"
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
        >
          <Loader2 className="w-12 h-12 text-primary mx-auto animate-spin mb-4" />
          <p className="text-slate-600">Cargando voucher...</p>
        </motion.div>
      </div>
    );
  }

  if (status === 'error') {
    return (
      <div className="min-h-screen gradient-hero flex items-center justify-center p-4">
        <motion.div 
          className="bg-white rounded-2xl p-8 max-w-md w-full text-center shadow-xl"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
        >
          <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <AlertCircle className="w-8 h-8 text-red-500" />
          </div>
          <h1 className="text-2xl font-bold text-slate-900 mb-2">Voucher no encontrado</h1>
          <p className="text-slate-600">{error}</p>
        </motion.div>
      </div>
    );
  }

  return (
    <div className="min-h-screen gradient-hero flex items-center justify-center p-4">
      <motion.div 
        className={`bg-white rounded-2xl p-8 max-w-md w-full shadow-xl ${voucher?.redeemed ? 'opacity-75' : ''}`}
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
      >
        {/* Status Badge */}
        {voucher?.redeemed && (
          <div className="bg-green-100 text-green-700 rounded-full px-4 py-2 text-sm font-medium text-center mb-4 flex items-center justify-center gap-2">
            <Check className="w-4 h-4" />
            Este voucher ya fue canjeado
          </div>
        )}

        {/* Header */}
        <div className="text-center mb-6">
          <motion.div 
            className={`w-20 h-20 rounded-full flex items-center justify-center mx-auto mb-4 ${
              voucher?.redeemed ? 'bg-green-100' : 'bg-primary/10'
            }`}
            animate={voucher?.redeemed ? {} : { scale: [1, 1.05, 1] }}
            transition={{ repeat: Infinity, duration: 2 }}
          >
            {voucher?.redeemed ? (
              <Check className="w-10 h-10 text-green-500" />
            ) : (
              <Gift className="w-10 h-10 text-primary" />
            )}
          </motion.div>
          <h1 className="text-2xl font-bold text-slate-900">
            {voucher?.redeemed ? '¡Voucher Canjeado!' : '¡Felicidades!'}
          </h1>
          <p className="text-slate-600 mt-1">{voucher?.campaign_name}</p>
        </div>

        {/* Reward */}
        <div className="bg-gradient-to-br from-primary to-indigo-600 rounded-xl p-6 text-white text-center mb-6">
          <p className="text-sm text-white/70 mb-1">Tu premio</p>
          <p className="text-2xl font-bold">{voucher?.reward}</p>
          {voucher?.reward_description && (
            <p className="text-sm text-white/80 mt-2 italic">
              {voucher.reward_description}
            </p>
          )}
        </div>

        {/* QR Code */}
        {!voucher?.redeemed && (
          <div className="bg-white rounded-xl p-6 border-2 border-dashed border-slate-200 flex flex-col items-center mb-6">
            <QRCodeSVG 
              value={voucher?.id || ''} 
              size={180}
              level="H"
            />
            <p className="text-sm text-slate-500 mt-4 text-center">
              Muestra este código en el establecimiento para canjear tu premio
            </p>
          </div>
        )}

        {/* Voucher ID */}
        <div className="text-center text-xs text-slate-400">
          <p>ID: {voucher?.id}</p>
          <p className="mt-1">
            Creado: {voucher?.created_at && new Date(voucher.created_at).toLocaleDateString('es-ES', {
              day: '2-digit',
              month: 'long',
              year: 'numeric'
            })}
          </p>
          {voucher?.redeemed_at && (
            <p className="mt-1 text-green-600">
              Canjeado: {new Date(voucher.redeemed_at).toLocaleDateString('es-ES', {
                day: '2-digit',
                month: 'long',
                year: 'numeric'
              })}
            </p>
          )}
        </div>

        {/* Instructions */}
        {!voucher?.redeemed && (
          <div className="mt-6 p-4 bg-amber-50 rounded-xl">
            <p className="text-sm text-amber-800 text-center">
              <strong>Importante:</strong> Este voucher solo puede canjearse una vez. 
              El personal del establecimiento lo marcará como usado al entregarte tu premio.
            </p>
          </div>
        )}
      </motion.div>
    </div>
  );
};

export default VoucherViewPage;
